import React, { useState, useEffect } from 'react';
import { ArrowLeft, Camera, X, Loader2, Wrench, Save } from 'lucide-react';
import { openNativeCamera, fileToDataUrl } from '../../utils/camera.utils';
import { Camera as CameraComponent } from '../Camera';
import { isIOS } from '../../utils/platform.utils';

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Language translations
const translations = {
  en: {
    title: 'Log Equipment Repair',
    whatWasRepaired: 'What was repaired?',
    required: '*',
    placeholder: 'e.g., Replaced AC compressor and recharged system',
    additionalDetails: 'Additional Details (Optional)',
    partsReplaced: 'Parts Replaced',
    partsPlaceholder: 'e.g., AC Compressor (Part# AC-12345), Refrigerant R-134a (2 lbs)',
    laborHours: 'Labor Hours',
    laborPlaceholder: 'e.g., 4.5',
    estimatedCost: 'Estimated Cost ($)',
    costPlaceholder: 'e.g., 850.00',
    additionalNotes: 'Additional Notes',
    notesPlaceholder: 'e.g., Tested system after installation. Cold air blowing at 40°F',
    photos: 'Photos (Optional)',
    takeAddPhoto: 'Take/Add Photo',
    photosAdded: 'photo(s) added',
    cancel: 'Cancel',
    submitRepair: 'Submit Repair',
    submitting: 'Submitting...',
    repairLogged: 'Repair Logged!',
    repairSuccess: 'has been documented successfully.',
    backToMachineHub: 'Back to Machine Hub',
    pleaseDescribe: 'Please describe what was repaired',
    machineParts: 'Service parts replaced',
    machinePartsHint: 'Check any of this machine\'s filters, belts or tracks you replaced. Each one is logged like a service change.',
    lastChanged: 'Last changed',
    neverChanged: 'No change on record',
    loadingParts: 'Loading parts...'
  },
  es: {
    title: 'Registrar Reparación',
    whatWasRepaired: '¿Qué fue reparado?',
    required: '*',
    placeholder: 'ej., Reemplacé el compresor de AC y recargué el sistema',
    additionalDetails: 'Detalles Adicionales (Opcional)',
    partsReplaced: 'Partes Reemplazadas',
    partsPlaceholder: 'ej., Compresor de AC (Parte# AC-12345), Refrigerante R-134a (2 lbs)',
    laborHours: 'Horas de Trabajo',
    laborPlaceholder: 'ej., 4.5',
    estimatedCost: 'Costo Estimado ($)',
    costPlaceholder: 'ej., 850.00',
    additionalNotes: 'Notas Adicionales',
    notesPlaceholder: 'ej., Probé el sistema después de la instalación. Aire frío a 40°F',
    photos: 'Fotos (Opcional)',
    takeAddPhoto: 'Tomar/Agregar Foto',
    photosAdded: 'foto(s) agregada(s)',
    cancel: 'Cancelar',
    submitRepair: 'Enviar Reparación',
    submitting: 'Enviando...',
    repairLogged: '¡Reparación Registrada!',
    repairSuccess: 'ha sido documentada exitosamente.',
    backToMachineHub: 'Volver al Centro de Máquinas',
    pleaseDescribe: 'Por favor describe qué fue reparado',
    machineParts: 'Partes de servicio reemplazadas',
    machinePartsHint: 'Marca los filtros, bandas o orugas de esta máquina que reemplazaste. Cada uno se registra como un cambio de servicio.',
    lastChanged: 'Último cambio',
    neverChanged: 'Sin cambio registrado',
    loadingParts: 'Cargando partes...'
  }
};

interface MachinePart {
  equipment_filter_id: number;
  filter_type_name: string;
  filter_category: string;
  part_number: string | null;
  is_active: boolean;
  last_changed_date: string | null;
  last_changed_hours: number | null;
}

interface EquipmentRepairsProps {
  equipment: {
    equipment_id: number;
    equipment_name: string;
    bradanna_equipment_number: string;
    equipment_type: string;
  };
  onBack: () => void;
}

const EquipmentRepairs: React.FC<EquipmentRepairsProps> = ({ equipment, onBack }) => {
  const [issueDescription, setIssueDescription] = useState('');
  const [partsReplaced, setPartsReplaced] = useState('');
  const [laborHours, setLaborHours] = useState('');
  const [estimatedCost, setEstimatedCost] = useState('');
  const [mechanicNotes, setMechanicNotes] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [cameraCooldown, setCameraCooldown] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  const t = translations[language];

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  // This machine's assigned service parts (filters/belts/tracks) so a swap done
  // outside an inspection can still be recorded as a service change.
  const [machineParts, setMachineParts] = useState<MachinePart[]>([]);
  const [partsLoading, setPartsLoading] = useState(true);
  const [selectedPartIds, setSelectedPartIds] = useState<number[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/inventory/equipment/${equipment.equipment_id}/filters`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('bfa_token')}` }
        });
        const data = await res.json();
        if (!cancelled && res.ok && data.success) {
          setMachineParts((data.data as MachinePart[]).filter(p => p.is_active));
        }
      } catch (err) {
        console.error('Error loading machine parts:', err);
      } finally {
        if (!cancelled) setPartsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [equipment.equipment_id]);

  const togglePart = (id: number) => {
    setSelectedPartIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Styles matching EquipmentInspection
  const styles = {
    container: {
      padding: '20px',
      paddingBottom: '40px',
      maxWidth: '800px',
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      backgroundColor: '#ea580c',
      color: 'white',
      padding: '16px',
      borderRadius: '12px 12px 0 0',
      marginBottom: '2px'
    },
    headerTop: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '8px'
    },
    backButton: {
      backgroundColor: 'rgba(255,255,255,0.2)',
      border: 'none',
      color: 'white',
      padding: '8px',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '14px'
    },
    headerTitle: {
      fontSize: '20px',
      fontWeight: '600',
      flex: 1
    },
    equipmentInfo: {
      fontSize: '14px',
      opacity: 0.9
    },
    content: {
      backgroundColor: 'white',
      borderRadius: '0 0 12px 12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    },
    formSection: {
      padding: '20px',
      borderBottom: '2px solid #f1f5f9'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '16px',
      fontWeight: '600',
      color: '#475569',
      marginBottom: '8px'
    },
    requiredLabel: {
      display: 'block',
      fontSize: '18px',
      fontWeight: '600',
      color: '#475569',
      marginBottom: '8px'
    } as React.CSSProperties,
    input: {
      width: '100%',
      padding: '14px 16px',
      fontSize: '18px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      boxSizing: 'border-box' as const
    },
    textarea: {
      width: '100%',
      padding: '14px 16px',
      fontSize: '18px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      minHeight: '100px',
      fontFamily: 'inherit',
      resize: 'vertical' as const,
      boxSizing: 'border-box' as const
    },
    photoSection: {
      padding: '20px',
      borderBottom: '2px solid #f1f5f9'
    },
    photoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: '12px',
      marginBottom: '12px'
    },
    photoPreview: {
      position: 'relative' as const,
      paddingTop: '100%',
      borderRadius: '8px',
      overflow: 'hidden',
      border: '2px solid #e2e8f0'
    },
    photoImg: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const
    },
    removePhotoButton: {
      position: 'absolute' as const,
      top: '6px',
      right: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '28px',
      height: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
    },
    photoButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      padding: '12px 20px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px'
    },
    bottomBar: {
      backgroundColor: '#f1f5f9',
      borderTop: '2px solid #e2e8f0',
      padding: '20px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '12px',
      borderRadius: '0 0 12px 12px'
    },
    saveButton: {
      backgroundColor: '#ea580c',
      color: 'white',
      border: 'none',
      padding: '10px 24px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      boxShadow: '0 2px 4px rgba(234, 88, 12, 0.3)',
      minWidth: '140px'
    },
    cancelButton: {
      backgroundColor: '#94a3b8',
      color: 'white',
      border: 'none',
      padding: '10px 24px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: '0 2px 4px rgba(148, 163, 184, 0.3)',
      minWidth: '100px'
    },
    successOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    successCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '32px',
      textAlign: 'center' as const,
      maxWidth: '400px',
      width: '100%'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }
  };

  const handleTakePhoto = async () => {
    if (cameraCooldown) return;

    // iOS: Use native camera (no permission prompts)
    // Android: Use custom camera (clean UI, no focus ring)
    if (isIOS()) {
      try {
        setCameraCooldown(true);
        await openNativeCamera(async (file) => {
          if (file && file instanceof File) {
            const previewUrl = await fileToDataUrl(file);
            setPhotos([...photos, file]);
            setPhotoPreviewUrls([...photoPreviewUrls, previewUrl]);
          }
        });
        setTimeout(() => setCameraCooldown(false), 1000);
      } catch (err) {
        console.error('Error capturing photo:', err);
        setError('Failed to capture photo. Please try again.');
        setCameraCooldown(false);
      }
    } else {
      // Android: Use custom camera component
      setShowCamera(true);
    }
  };

  const handleCameraCapture = async (imageDataUrl: string) => {
    setShowCamera(false);

    try {
      // Convert base64 to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      const file = new File([blob], `repair-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const previewUrl = await fileToDataUrl(file);

      setPhotos([...photos, file]);
      setPhotoPreviewUrls([...photoPreviewUrls, previewUrl]);

      // Cooldown
      setCameraCooldown(true);
      setTimeout(() => setCameraCooldown(false), 1000);
    } catch (err) {
      console.error('Error processing camera capture:', err);
      setError('Failed to process photo. Please try again.');
      setCameraCooldown(false);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    setCameraCooldown(true);
    setTimeout(() => setCameraCooldown(false), 500);
  };

  const removePhoto = (index: number) => {
    const newPhotos = [...photos];
    const newPreviewUrls = [...photoPreviewUrls];

    URL.revokeObjectURL(newPreviewUrls[index]);

    newPhotos.splice(index, 1);
    newPreviewUrls.splice(index, 1);

    setPhotos(newPhotos);
    setPhotoPreviewUrls(newPreviewUrls);
  };

  const handleSubmit = async () => {
    // Validation
    if (!issueDescription.trim()) {
      setError(t.pleaseDescribe);
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('equipment_id', equipment.equipment_id.toString());
      formData.append('issue_description', issueDescription);
      formData.append('repair_performed', issueDescription); // Use same value for backwards compatibility
      if (partsReplaced.trim()) formData.append('parts_replaced', partsReplaced);
      if (laborHours) formData.append('labor_hours', laborHours);
      if (estimatedCost) formData.append('estimated_cost', estimatedCost);
      if (mechanicNotes.trim()) formData.append('mechanic_notes', mechanicNotes);
      if (selectedPartIds.length) formData.append('replaced_filter_ids', JSON.stringify(selectedPartIds));
      formData.append('status', 'completed');

      photos.forEach((photo) => {
        formData.append('photos', photo);
      });

      const response = await fetch(`${API_BASE_URL}/api/equipment/repairs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Failed to submit repair');
      }
    } catch (err: any) {
      console.error('Error submitting repair:', err);
      setError(err.message || 'Failed to submit repair');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={styles.successOverlay}>
        <div style={styles.successCard}>
          <Wrench size={64} style={{ color: '#ea580c', margin: '0 auto 16px auto' }} />
          <h2 style={{ margin: '0 0 8px 0', color: '#ea580c' }}>{t.repairLogged}</h2>
          <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>
            {equipment.equipment_name} {t.repairSuccess}
          </p>
          <button onClick={onBack} style={styles.saveButton}>
            {t.backToMachineHub}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.headerTitle}>{t.title}</div>
        </div>
        <div style={styles.equipmentInfo}>
          {equipment.equipment_name} (#{equipment.bradanna_equipment_number})
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Required Fields Section */}
        <div style={styles.formSection}>
          <div style={styles.formGroup}>
            <label style={styles.requiredLabel}>
              {t.whatWasRepaired} {t.required}
            </label>
            <textarea
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              style={styles.textarea}
              placeholder={t.placeholder}
            />
          </div>
        </div>

        {/* Optional Fields Section */}
        <div style={styles.formSection}>
          <div style={styles.sectionTitle}>{t.additionalDetails}</div>

          <div style={styles.formGroup}>
            <label style={styles.label}>{t.partsReplaced}</label>
            <textarea
              value={partsReplaced}
              onChange={(e) => setPartsReplaced(e.target.value)}
              style={styles.textarea}
              placeholder={t.partsPlaceholder}
              rows={2}
            />
          </div>

          {/* Assigned service parts: checking one writes the same history row a
              kiosk inspection "Changed" would, so the due clock resets. */}
          {(partsLoading || machineParts.length > 0) && (
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.machineParts}</label>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>{t.machinePartsHint}</div>
              {partsLoading ? (
                <div style={{ fontSize: '14px', color: '#94a3b8' }}>{t.loadingParts}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {machineParts.map(p => {
                    const checked = selectedPartIds.includes(p.equipment_filter_id);
                    const last = p.last_changed_date
                      ? `${t.lastChanged}: ${new Date(p.last_changed_date).toLocaleDateString()}${p.last_changed_hours != null ? ` @ ${p.last_changed_hours.toLocaleString()} h` : ''}`
                      : t.neverChanged;
                    return (
                      <label
                        key={p.equipment_filter_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 14px',
                          border: `2px solid ${checked ? '#ea580c' : '#e2e8f0'}`,
                          backgroundColor: checked ? '#fff7ed' : 'white',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePart(p.equipment_filter_id)}
                          style={{ width: '22px', height: '22px', accentColor: '#ea580c', flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                            {p.filter_type_name}
                            {p.part_number && <span style={{ fontWeight: 400, color: '#64748b' }}> · {p.part_number}</span>}
                          </div>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>{last}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.laborHours}</label>
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9]*\.?[0-9]*"
                value={laborHours}
                onChange={(e) => {
                  // Allow numbers and decimal point
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setLaborHours(value);
                }}
                style={styles.input}
                placeholder={t.laborPlaceholder}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{t.estimatedCost}</label>
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9]*\.?[0-9]*"
                value={estimatedCost}
                onChange={(e) => {
                  // Allow numbers and decimal point
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setEstimatedCost(value);
                }}
                style={styles.input}
                placeholder={t.costPlaceholder}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>{t.additionalNotes}</label>
            <textarea
              value={mechanicNotes}
              onChange={(e) => setMechanicNotes(e.target.value)}
              style={styles.textarea}
              placeholder={t.notesPlaceholder}
              rows={2}
            />
          </div>
        </div>

        {/* Photos Section */}
        <div style={styles.photoSection}>
          <div style={styles.sectionTitle}>
            <Camera size={20} />
            {t.photos}
          </div>

          {photoPreviewUrls.length > 0 && (
            <div style={styles.photoGrid}>
              {photoPreviewUrls.map((url, index) => (
                <div key={index} style={styles.photoPreview}>
                  <img src={url} alt={`Repair photo ${index + 1}`} style={styles.photoImg} />
                  <button onClick={() => removePhoto(index)} style={styles.removePhotoButton}>
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={handleTakePhoto}
            disabled={cameraCooldown}
            style={{
              ...styles.photoButton,
              opacity: cameraCooldown ? 0.5 : 1,
              cursor: cameraCooldown ? 'not-allowed' : 'pointer'
            }}
          >
            <Camera size={18} />
            <span>{t.takeAddPhoto}</span>
          </button>

          {photos.length > 0 && (
            <p style={{ fontSize: '16px', color: '#64748b', marginTop: '12px' }}>
              {photos.length} {t.photosAdded}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div style={styles.bottomBar}>
        <button onClick={onBack} style={styles.cancelButton}>
          <X size={20} />
          {t.cancel}
        </button>
        <button onClick={handleSubmit} disabled={isSubmitting} style={styles.saveButton}>
          {isSubmitting ? (
            <>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
              {t.submitting}
            </>
          ) : (
            <>
              <Save size={20} />
              {t.submitRepair}
            </>
          )}
        </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          position: 'fixed',
          top: '100px',
          left: '20px',
          right: '20px',
          backgroundColor: '#fef2f2',
          border: '2px solid #fca5a5',
          color: '#dc2626',
          padding: '16px 20px',
          borderRadius: '8px',
          zIndex: 1000,
          textAlign: 'left',
          fontSize: '16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <button
            onClick={() => setError(null)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'none',
              border: 'none',
              color: '#dc2626',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={20} />
          </button>
          {error}
        </div>
      )}

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Camera Component (Android only) */}
      {showCamera && (
        <CameraComponent
          onCapture={handleCameraCapture}
          onClose={handleCameraClose}
        />
      )}
    </div>
  );
};

export default EquipmentRepairs;
